import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { freeToolsAPI, jobTrackerAPI, authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { CreditCounter } from './CreditComponents';
import logo from '../assets/logo.png';

/**
 * MatchScore Page - Dedicated page for the Match Score tool
 * Accessible to both authenticated and non-authenticated users
 * Does NOT redirect authenticated users away (unlike Home page)
 */

// Embedded MatchLite Component (same as in Home.jsx)
const EmbeddedMatchLite = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = React.useState({
    jobDescription: '',
    resumeText: ''
  });
  const [resumeFile, setResumeFile] = React.useState(null);
  const [results, setResults] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [savingToTracker, setSavingToTracker] = React.useState(false);
  const [savedToTracker, setSavedToTracker] = React.useState(false);

  // Handle Google Login for non-authenticated users
  const handleGoogleLogin = () => {
    // Set flag to return to match score page after login
    localStorage.setItem('login_redirect', 'match_score');
    authAPI.initiateGoogleLogin('/auth/callback');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a PDF, DOC, DOCX, or TXT file');
        return;
      }
      setResumeFile(file);
      setFormData(prev => ({ ...prev, resumeText: '' }));
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('job_description', formData.jobDescription);
      
      if (resumeFile) {
        submitData.append('resume_file', resumeFile);
      } else if (formData.resumeText) {
        submitData.append('resume_text', formData.resumeText);
      } else {
        setError('Please provide a resume (file or text)');
        setLoading(false);
        return;
      }

      const response = await freeToolsAPI.matchLite(submitData);
      setResults(response);
      
      // Dispatch credit update event
      window.dispatchEvent(new CustomEvent('credit_update'));
    } catch (err) {
      console.error('Match analysis error:', err);
      if (err.response?.status === 402) {
        const detail = err.response?.data?.detail;
        setError(detail?.message || 'No credits remaining. Please purchase more credits to continue.');
      } else if (err.response?.status === 429) {
        setError('Daily limit reached. Please try again tomorrow.');
      } else {
        setError(err.response?.data?.detail || err.message || 'Analysis failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToTracker = async () => {
    if (!results || !isAuthenticated) return;
    
    setSavingToTracker(true);
    try {
      // Extract company and title from job description (simple heuristic)
      const lines = formData.jobDescription.split('\n').filter(l => l.trim());
      const company = lines[0]?.substring(0, 50) || 'Unknown Company';
      const title = lines[1]?.substring(0, 50) || 'Unknown Position';
      
      await jobTrackerAPI.createApplication({
        company,
        title,
        status: 'saved',
        job_description: formData.jobDescription,
        match_score: results.match_score,
        match_analysis: results,
        notes: `Match Score: ${results.match_score}%`
      });
      
      setSavedToTracker(true);
      
      // Navigate to tracker after short delay
      setTimeout(() => {
        navigate('/tracker');
      }, 1500);
    } catch (err) {
      console.error('Failed to save to tracker:', err);
      setError('Failed to save to tracker. Please try again.');
    } finally {
      setSavingToTracker(false);
    }
  };

  const resetForm = () => {
    setResults(null);
    setFormData({ jobDescription: '', resumeText: '' });
    setResumeFile(null);
    setSavedToTracker(false);
    setError('');
  };

  // Results View
  if (results) {
    const score = results.match_score || 0;
    const scoreColor = score >= 80 ? 'text-green-600' : score >= 60 ? 'text-yellow-600' : 'text-red-600';
    const scoreBg = score >= 80 ? 'bg-green-50 border-green-200' : score >= 60 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200';

    return (
      <div className="space-y-6">
        {/* Score Display */}
        <div className={`p-6 rounded-xl border-2 ${scoreBg} text-center`}>
          <p className="text-sm text-gray-600 mb-2">Your Match Score</p>
          <p className={`text-5xl font-bold ${scoreColor}`}>{score}%</p>
          <p className="text-sm text-gray-500 mt-2">
            {score >= 80 ? 'Excellent match!' : score >= 60 ? 'Good potential' : 'Needs improvement'}
          </p>
        </div>

        {/* Summary */}
        {results.summary && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Summary</h4>
            <p className="text-sm text-gray-600">{results.summary}</p>
          </div>
        )}

        {/* Key Findings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {results.matching_skills?.length > 0 && (
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-medium text-green-800 mb-2">Matching Skills</h4>
              <ul className="text-sm text-green-700 space-y-1">
                {results.matching_skills.slice(0, 5).map((skill, i) => (
                  <li key={i}>✓ {skill}</li>
                ))}
              </ul>
            </div>
          )}
          
          {results.missing_skills?.length > 0 && (
            <div className="p-4 bg-red-50 rounded-lg">
              <h4 className="font-medium text-red-800 mb-2">Skills to Develop</h4>
              <ul className="text-sm text-red-700 space-y-1">
                {results.missing_skills.slice(0, 5).map((skill, i) => (
                  <li key={i}>○ {skill}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          {isAuthenticated && !savedToTracker && (
            <button
              onClick={handleSaveToTracker}
              disabled={savingToTracker}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {savingToTracker ? 'Saving...' : 'Save this Job to Tracker'}
            </button>
          )}
          {savedToTracker && (
            <div className="flex-1 px-4 py-3 bg-green-100 text-green-800 rounded-lg text-center font-medium">
              ✓ Saved! Redirecting to tracker...
            </div>
          )}
          <button
            onClick={resetForm}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
          >
            Analyze Another Job
          </button>
        </div>
      </div>
    );
  }

  // Form View
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Job Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Job Description *
        </label>
        <textarea
          name="jobDescription"
          value={formData.jobDescription}
          onChange={handleInputChange}
          placeholder="Paste the full job description here..."
          className="w-full h-40 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          required
        />
      </div>

      {/* Resume Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Your Resume *
        </label>
        
        {/* File Upload */}
        <div className="mb-3">
          <input
            type="file"
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx,.txt"
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          {resumeFile && (
            <p className="mt-1 text-sm text-green-600">✓ {resumeFile.name}</p>
          )}
        </div>

        {/* Or divider */}
        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">or paste text</span>
          </div>
        </div>

        {/* Text Input */}
        <textarea
          name="resumeText"
          value={formData.resumeText}
          onChange={handleInputChange}
          placeholder="Paste your resume text here..."
          className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          disabled={!!resumeFile}
        />
      </div>

      {/* Submit Button */}
      <div>
        {isAuthenticated ? (
          <button
            type="submit"
            disabled={loading || (!formData.jobDescription || (!resumeFile && !formData.resumeText))}
            className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Analyzing...
              </span>
            ) : (
              'Check My Match Score'
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleGoogleLogin}
            className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google to Scan
          </button>
        )}
      </div>

      {/* Privacy Notice & AI Disclaimer */}
      <div className="mt-4 space-y-2 text-center">
        <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          <span><strong>Zero Data Retention:</strong> Your resume and job description are processed for this analysis and then immediately discarded. We do not store your personal data.</span>
        </p>
        <p className="text-xs text-gray-400 italic">
          AI-generated results may vary. Please review all suggestions before applying.
        </p>
      </div>

      {/* Info text for non-authenticated */}
      {!isAuthenticated && (
        <div className="text-center">
          <p className="text-xs text-gray-500">
            Login with Google to unlock your 5 free lifetime credits
          </p>
        </div>
      )}
    </form>
  );
};

// Main MatchScore Page Component
const MatchScore = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // Sign In handler
  const handleSignIn = () => {
    authAPI.initiateGoogleLogin('/auth/callback');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="AlignifyNOW" className="h-[80px] w-auto" />
              <span className="text-lg font-bold tracking-tight text-slate-900 hidden sm:inline">
                Alignify<span className="text-blue-600">NOW</span>
              </span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/tracker" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Job Tracker
              </Link>
              <Link to="/tools/cover-letter" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Cover Letter
              </Link>
              <Link to="/pricing" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Pricing
              </Link>
              
              {isAuthenticated ? (
                <>
                  <CreditCounter />
                  {user && (
                    <div className="hidden md:flex items-center gap-2 ml-4 border-l pl-4 border-gray-200">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-gray-200" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs">
                          {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="text-sm font-medium text-gray-700">
                        {user.name || user.email}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <button
                  onClick={handleSignIn}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Match Score
          </h1>
          <p className="text-lg text-gray-600">
            See how well your resume matches a job description
          </p>
        </div>

        {/* Match Tool Card */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 sm:p-8">
          <EmbeddedMatchLite />
        </div>

        {/* Info Section */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Each scan uses 1 credit. New users get 5 free credits at signup.</p>
          <Link to="/pricing" className="text-blue-600 hover:underline">
            Need more credits? View pricing →
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <span className="text-xl font-bold text-white opacity-80">
                Alignify<span className="text-blue-400">NOW</span>
              </span>
            </div>
            <div className="text-gray-400 text-xs mb-4 md:mb-0">
              © 2026 AlignifyNOW. All rights reserved. Built by Matambanadzo Studio.
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">
                Terms of Service
              </Link>
              <Link to="/affiliates" className="text-gray-400 hover:text-white text-sm transition-colors">
                Affiliates
              </Link>
              <Link to="/checklist" className="text-gray-400 hover:text-white text-sm transition-colors">
                Free Checklist
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MatchScore;
